import * as React from 'react';
import cn from 'classnames';

import { useTheme } from '../../../../theme';
import { Baggage } from '../../../../BaggageService/types';
import Money from '../../../../Money';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import { Segment } from '../../../types';
import { useTranslation } from 'react-i18next';
import { isBaggageExcess as getIsBaggageExcess } from '../../utils';

interface Props {
	className?: string;
	segment: Segment;
	baggageByRfics: Baggage[][];
	baggageTab?: string;
	mobileSelectedServiceId?: string;
	isAvailable?: boolean;
	valueAlign?: 'center' | 'right';
}

const BaggageSegment: React.FC<Props> = ({
	segment,
	mobileSelectedServiceId,
	baggageByRfics,
	baggageTab,
	isAvailable,
	valueAlign
}) => {
	const from = segment.departure.airport.city?.name;
	const to = segment.arrival.airport.city?.name;

	const theme = useTheme('UpgradeBaggageDialog').BaggageSegment;
	const { t } = useTranslation('IncludedBaggage');

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const isUnavailable = isAvailable === false;

	const baggagesWithOtherPrices = React.useMemo(() => {
		if (baggageByRfics.length) {
			const firstBaggage = baggageByRfics[0][0];
			const baggageWithOtherPrice = baggageByRfics.find(baggages =>
				baggages.some(baggage => baggage?.price.amount !== firstBaggage.price.amount)
			);

			return !!baggageWithOtherPrice || baggageByRfics[0]?.length === 1;
		}

		return !!baggageTab;
	}, [baggageByRfics]);

	const selectedBaggage: Baggage & { withPriceFrom?: boolean } = React.useMemo(() => {
		const selectedTab = isMobile ? baggageTab ?? mobileSelectedServiceId : baggageTab;

		if (selectedTab) {
			// ищем доступный багаж с мин ценой
			let actualBaggage: Baggage = null;
			let withFrom = false;

			baggageByRfics
				.find(baggages => baggages.find(bagggage => bagggage.rfics === selectedTab))
				?.forEach(bag => {
					const isTargetSegment = bag.segmentIds.some(segments =>
						segments.some(segmentId => segmentId === segment.id)
					);
					if (isTargetSegment && bag.canBeAdded) {
						if (
							actualBaggage &&
							(actualBaggage.price.amount > bag.price.amount ||
								actualBaggage.price.amount < bag.price.amount)
						) {
							withFrom = true;
						}

						if (!actualBaggage || bag.price.amount < actualBaggage.price.amount) {
							actualBaggage = bag;
						}
					}
				});

			if (!actualBaggage || (actualBaggage && getIsBaggageExcess(actualBaggage))) {
				return null;
			}

			return {
				...actualBaggage,
				withPriceFrom: withFrom
			};
		}

		return null;
	}, [baggageTab, mobileSelectedServiceId, baggageByRfics, segment, isMobile]);

	return (
		<div className={cn(theme.root, { [theme.disabled]: isUnavailable })}>
			<div
				className={cn(theme.name, {
					[theme.name_withPrices]: baggagesWithOtherPrices
				})}
			>
				{from} &mdash; {to}
			</div>

			{(baggagesWithOtherPrices || isMobile) && (
				<div className={cn(theme.prices, { [theme.prices_rightAlign]: valueAlign === 'right' })}>
					<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
						{baggageByRfics.map(baggagesWithRfisc => {
							// ищем доступный багаж с мин ценой
							let baggage: Baggage = null;
							let withFrom = false;

							baggagesWithRfisc.forEach(bag => {
								const isTargetSegment =
									isUnavailable ||
									(!isUnavailable &&
										bag.segmentIds.some(segments =>
											segments.some(segmentId => segmentId === segment.id)
										));

								if (isTargetSegment && bag.canBeAdded) {
									if (
										baggage &&
										(bag.price.amount < baggage.price.amount ||
											bag.price.amount > baggage.price.amount)
									) {
										withFrom = true;
									}

									if (!baggage || bag.price.amount < baggage.price.amount) {
										baggage = bag;
									}
								}
							});

							if (!baggage) {
								return <div className={theme.price__empty} />;
							}

							const isBaggageExcess = getIsBaggageExcess(baggage);
							const hidePrice = isBaggageExcess;

							return (
								<div key={baggage.id} className={theme.price}>
									{!hidePrice && (
										<>
											{withFrom && t('from')} <Money money={baggage.price} />
										</>
									)}
								</div>
							);
						})}
					</MediaQuery>

					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						<div className={selectedBaggage ? theme.price : theme.price__empty}>
							{selectedBaggage && (
								<>
									{selectedBaggage.withPriceFrom && t('from')}{' '}
									<Money money={selectedBaggage?.price} />
								</>
							)}
						</div>
					</MediaQuery>
				</div>
			)}

			{!baggagesWithOtherPrices && !isMobile && (
				<div className={theme.price}>
					{selectedBaggage?.withPriceFrom && t('from')} <Money money={selectedBaggage?.price} />
				</div>
			)}
		</div>
	);
};

export default BaggageSegment;
